import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const INTERACTION_DELAY = 200;

interface UseToastContainerInteractionsProps {
  readonly expanded: boolean;
  readonly setExpanded: (v: boolean) => void;
  readonly setInteracting: (v: boolean) => void;
  readonly toastCount: number;
}

export const useToastContainerInteractions = ({
  expanded,
  setExpanded,
  setInteracting,
  toastCount,
}: UseToastContainerInteractionsProps) => {
  const pointerUpTimerRef = useRef<number | null>(null);
  const touchEndTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pointerUpTimerRef.current != null) {
        clearTimeout(pointerUpTimerRef.current);
      }

      if (touchEndTimerRef.current != null) {
        clearTimeout(touchEndTimerRef.current);
      }
    };
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      if (pointerUpTimerRef.current != null) {
        clearTimeout(pointerUpTimerRef.current);
      }

      pointerUpTimerRef.current = window.setTimeout(() => setInteracting(false), INTERACTION_DELAY);
    },
    [setInteracting],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      if (touchEndTimerRef.current != null) {
        clearTimeout(touchEndTimerRef.current);
      }

      touchEndTimerRef.current = window.setTimeout(() => {
        setInteracting(false);
      }, INTERACTION_DELAY);
    },
    [setInteracting],
  );

  return useMemo(
    () => ({
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(true);
        setInteracting(true);
      },
      onMouseEnter: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!expanded) {
          setExpanded(true);
        }
      },
      onMouseLeave: (e: React.MouseEvent) => {
        e.stopPropagation();
        const relatedTarget = e.relatedTarget as Node | null;
        /* istanbul ignore else -- @preserve - Edge case: mouse leaving to child element is untestable */
        if (relatedTarget == null || !(relatedTarget instanceof Node) || !e.currentTarget.contains(relatedTarget)) {
          if (toastCount > 1) {
            setExpanded(false);
          }

          setInteracting(false);
        }
      },
      onMouseMove: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!expanded) {
          setExpanded(true);
        }
      },
      onPointerDown: (e: React.PointerEvent) => {
        e.stopPropagation();
        setInteracting(true);
      },
      onPointerUp: handlePointerUp,
      onTouchEnd: handleTouchEnd,
      onTouchStart: (e: React.TouchEvent) => {
        e.stopPropagation();
        setExpanded(true);
        setInteracting(true);
      },
    }),
    [expanded, setExpanded, setInteracting, toastCount, handlePointerUp, handleTouchEnd],
  );
};
